import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Palette, Building2, Stethoscope, Users } from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";
import { PLATEFORME_MODULES } from "../data/plateformeModules";

const PERSONAS = [
  { to: "/studio/cabinet-liberal", icon: Stethoscope, t: "Cabinet libéral", d: "Praticien seul ou en groupe : téléconsultation, agenda, quota télésoin." },
  { to: "/studio/msp", icon: Users, t: "Maison de santé (MSP)", d: "Coordination pluripro, messagerie d'équipe, facturation mutualisée." },
  { to: "/studio/cpts", icon: Building2, t: "CPTS & territoires", d: "Réseau de proximité, adressage, parcours de soins territoriaux." },
];

export default function Plateforme() {
  return (
    <div style={{ background: "#F8FAFC" }} data-testid="page-plateforme">
      <SEO
        title="Plateforme de télésanté en marque blanche · MetaConnect"
        description="Plateforme de télésanté à votre marque : agenda, MSSanté, compteur réglementaire, facturation Factur-X, réseau de proximité. Hébergement HDS, conforme ANS."
        canonical="/plateforme"
        schema={breadcrumb([
          { name: "Accueil", url: "/" },
          { name: "Plateforme", url: "/plateforme" },
        ])}
      />

      {/* Hero */}
      <section className="relative pt-16" data-testid="plateforme-hero">
        <div className="grid lg:grid-cols-2 min-h-[480px]">
          <div className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 flex flex-col justify-center" style={{ background: "#0F2B5B" }}>
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
                 style={{ background: "rgba(79,195,247,0.12)", border: "1px solid rgba(79,195,247,0.30)", color: "#4FC3F7",
                          fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              <Palette size={12} /> Marque blanche
            </div>
            <h1 className="mt-6 font-display" style={{ color: "#fff", fontSize: "clamp(32px, 4.2vw, 52px)", fontWeight: 600, lineHeight: 1.08, letterSpacing: "-0.018em" }}>
              Votre plateforme de télésanté, <span style={{ color: "#4FC3F7" }}>à votre marque</span>.
            </h1>
            <p className="mt-6 max-w-md" style={{ color: "#93B4D4", fontSize: 16, lineHeight: 1.55 }}>
              Téléconsultation, agenda en ligne, MSSanté, dossiers, conformité 2026 : une plateforme
              complète déployée sous votre nom et votre logo, hébergée HDS en France.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/studio" data-testid="plateforme-cta-studio"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-medium transition hover:brightness-110"
                    style={{ background: "#4FC3F7", color: "#0F2B5B" }}>
                Configurer en 10 minutes <ArrowRight size={15} />
              </Link>
              <Link to="/demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-medium border border-white/30 text-white hover:bg-white/10 transition">
                Planifier une démo
              </Link>
            </div>
          </div>
          <div className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 flex flex-col justify-center bg-white">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0284C7]">Inclus dans le socle</div>
            <div className="mt-5 grid gap-3">
              {["Téléconsultation vidéo sécurisée (salle d'attente virtuelle)",
                "Agenda & prise de rendez-vous en ligne patients",
                "Messageries MSSanté individuelles et organisationnelles",
                "Site vitrine public généré pour votre structure",
                "Authentification Pro Santé Connect (e-CPS)",
                "Hébergement HDS 🇫🇷 · référentiel socle télésanté ANS"].map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="grid place-items-center h-5 w-5 rounded-full bg-[#E0F2FE] text-[#0284C7] shrink-0 mt-0.5"><ShieldCheck size={11} /></span>
                  <p className="text-[13.5px] text-[#334155]">{f}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-16" data-testid="plateforme-modules">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0284C7]">Modules différenciants</div>
          <h2 className="mt-2 font-display text-[#0F2B5B] text-lg font-semibold">
            Là où les autres plateformes s'arrêtent, la vôtre continue
          </h2>
          <div className="mt-7 grid sm:grid-cols-2 gap-4">
            {Object.entries(PLATEFORME_MODULES).map(([slug, m]) => {
              const Icon = m.icon;
              return (
                <Link key={slug} to={`/plateforme/${slug}`} data-testid={`plateforme-module-${slug}`}
                      className="group rounded-2xl border border-[#E2E8F0] bg-white p-6 hover:border-[#4FC3F7] hover:shadow-[0_16px_40px_-20px_rgba(15,43,91,0.25)] transition">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#F0F9FF] border border-[#BAE6FD] text-[#0284C7]">
                      <Icon size={20} />
                    </span>
                    <div className="text-[15.5px] font-semibold text-[#0F2B5B]">{m.nav}</div>
                  </div>
                  <p className="mt-3 text-[13px] text-[#475569] leading-relaxed line-clamp-3">{m.intro}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0284C7] group-hover:gap-2.5 transition-all">
                    Découvrir le module <ArrowRight size={13} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className="pb-16" data-testid="plateforme-personas">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0284C7]">Pensée pour votre exercice</div>
          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            {PERSONAS.map((p) => (
              <Link key={p.to} to={p.to} data-testid={`plateforme-persona-${p.to.split("/").pop()}`}
                    className="group rounded-xl border border-[#E2E8F0] bg-white p-5 hover:border-[#4FC3F7] transition">
                <p.icon size={18} className="text-[#0284C7]" />
                <div className="mt-2.5 text-[14px] font-semibold text-[#0F2B5B]">{p.t}</div>
                <p className="mt-1 text-[12.5px] text-[#475569] leading-relaxed">{p.d}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-[12px] text-[#0284C7] group-hover:gap-2 transition-all">
                  Voir le parcours <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl p-8 lg:p-10 flex flex-wrap items-center justify-between gap-5" style={{ background: "#0F2B5B" }} data-testid="plateforme-final-cta">
            <div>
              <div className="inline-flex items-center gap-2 text-white font-display text-[20px] font-semibold">
                <Sparkles size={18} style={{ color: "#4FC3F7" }} /> Votre sandbox personnalisée, en 10 minutes
              </div>
              <p className="mt-2 text-[13.5px] max-w-xl" style={{ color: "#93B4D4" }}>
                Choisissez vos services, votre identité visuelle et vos workflows : le Studio génère
                immédiatement un aperçu interactif complet de votre future plateforme.
              </p>
            </div>
            <Link to="/studio" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[14px] font-medium transition hover:brightness-110"
                  style={{ background: "#4FC3F7", color: "#0F2B5B" }}>
              Ouvrir le Studio <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
